import { ApolloError, gql } from '@apollo/client';
import { comparePacksByUnit, ResolvedPackSort } from '@/utils/pack-sort';
import { DEFAULT_CURRENCY_CODE, getCountryCurrencyCode } from '@/utils/money';
import { type EmployeeRestaurant } from '@/contexts/user-context';
import { getPackDisplayString } from '@/utils/item';
import {
  GetRelatedVariantPacks_VariantPackInfoFieldsFragment,
  GetRelatedVariantPacks_VariantPackInfoFragment,
  GetRelatedVariantPacksIdsQuery,
  GetVariantPackQuery,
  GetVariantPackQueryVariables,
  VariantPackInfoContext_GroupBadgeFieldsFragment,
} from '@/types';
import { PackUnit, VariantPackAvailabilityStatus } from '@/enums';
import { PRICE_EDIT_DISPLAY_INFO_FRAGMENT } from '@/components/orders/hooks/use-get-price-edit-display-info';
import { useGetDynamicConfigValueForChat } from '@/hooks/use-dynamic-config-value';
import { useMemo } from 'react';
import { usePackSortConfig } from '@/hooks/use-pack-sort-config';
import { useToasts } from '@/contexts/toast-context';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

export const VARIANT_PACK_INFO_FRAGMENT = gql`
  fragment VariantPackInfoBase on GetVariantPackInfoResponse {
    availability_status
    available_quantity
    cost_info {
      details {
        cost_in_micros
        type
      }
    }
    currency_code
    inventory_info {
      quantity_on_hand
    }
    variant_pack {
      external_item_id
      item {
        uuid
        name
        description
        photo_url_list
      }
      metadata
      pack {
        unit
        max_unit_size
        min_unit_size
        uuid
      }
      uuid
    }
    variant_pack_id
  }
`;

export const VARIANT_PACK_PRICING_INFO_FRAGMENT = gql`
  fragment VariantPackPricingInfo on GetVariantPackInfoResponse {
    price_in_micros
    sales_rep_price_in_micros
    sales_rep_gross_profit_in_percentage
  }
`;

export const GET_VARIANT_PACK_QUERY = gql`
  query getVariantPack(
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $variantPackUUID: uuid!
  ) {
    getVariantPackInfoList(
      supplier_id: $supplierUUID
      restaurant_id: $restaurantUUID
      variant_pack_id_list: [$variantPackUUID]
    ) {
      ...VariantPackInfoBase
    }

    businesses_by_pk(uuid: $supplierUUID) {
      address {
        country_code
        uuid
      }
      supplier_chats(where: { restaurant_uuid: { _eq: $restaurantUUID } }) {
        item_modifiers_enabled
        uuid
      }
      supplier_metadata {
        display_item_code
        uuid
      }
      uuid
    }
  }

  ${VARIANT_PACK_INFO_FRAGMENT}
`;

export const GET_VARIANT_PACK_PRICING_INFO_QUERY = gql`
  query getVariantPackPricingInfo(
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $variantPackUUID: uuid!
  ) {
    getVariantPackInfoList(
      supplier_id: $supplierUUID
      restaurant_id: $restaurantUUID
      variant_pack_id_list: [$variantPackUUID]
    ) {
      ...VariantPackInfoBase
      ...VariantPackPricingInfo
    }

    getPriceEditDisplayInfo(
      supplier_id: $supplierUUID
      restaurant_id: $restaurantUUID
      variant_pack_ids: [$variantPackUUID]
    ) {
      ...PriceEditing_PriceEditDisplayInfo
    }
  }

  ${VARIANT_PACK_INFO_FRAGMENT}
  ${VARIANT_PACK_PRICING_INFO_FRAGMENT}
  ${PRICE_EDIT_DISPLAY_INFO_FRAGMENT}
`;

export const GET_RELATED_VARIANT_PACKS_IDS_QUERY = gql`
  query getRelatedVariantPacksIds(
    $filters: variants_packs_bool_exp!
    $supplierUUID: uuid!
    $restaurantUUID: uuid!
  ) {
    variants_packs(
      where: {
        visible_variant_pack: {
          restaurant_uuid: { _eq: $restaurantUUID }
          supplier_uuid: { _eq: $supplierUUID }
          variant_pack: $filters
        }
      }
    ) {
      uuid
      external_item_id
    }
  }
`;

export const GET_RELATED_VARIANT_PACKS_QUERY = gql`
  fragment GetRelatedVariantPacks_VariantPackInfoFields on variants_packs {
    uuid
    external_item_id
    pack {
      unit
      max_unit_size
      min_unit_size
      uuid
    }
  }

  fragment GetRelatedVariantPacks_VariantPackInfo on GetVariantPackInfoResponse {
    variant_pack {
      ...GetRelatedVariantPacks_VariantPackInfoFields
    }
    price_in_micros
    currency_code
    unit_count
  }

  query getRelatedVariantPacks(
    $variantPackUUIDs: [uuid!]!
    $supplierUUID: uuid!
    $restaurantUUID: uuid!
    $deliveryDate: String
  ) {
    getVariantPackInfoList(
      supplier_id: $supplierUUID
      restaurant_id: $restaurantUUID
      variant_pack_id_list: $variantPackUUIDs
      delivery_date: $deliveryDate
    ) {
      ...GetRelatedVariantPacks_VariantPackInfo
    }
  }
`;

/**
 * Interface containing variant pack information.
 */
export interface VariantPackInformation {
  /**
   * Availability status of the variant pack.
   */
  availabilityStatus: VariantPackAvailabilityStatus | null;

  /**
   * Quantity of the variant pack that's available.
   */
  availableQuantity: number | null;

  /**
   * Badges associated with the variant pack.
   */
  badges: VariantPackInfoContext_GroupBadgeFieldsFragment[];

  /**
   * Currency code of the variant pack.
   */
  currencyCode: string;

  /**
   * Customer cost in micros of the variant pack.
   */
  customerCostInMicros: number | null;

  /**
   * Description of the variant pack.
   */
  description: string | null;

  /**
   * Display item code.
   */
  displayItemCode: boolean;

  /**
   * External item ID of the variant pack.
   */
  externalItemId: string | null;

  /**
   * Item modifiers enabled.
   */
  itemModifiersEnabled: boolean;

  /**
   * Name of the variant pack.
   */
  name: string;

  /**
   * Pack unit of the variant pack.
   */
  packUnit: PackUnit | null;

  /**
   * Photo URLs of the variant pack.
   */
  photoURLs: string[];

  /**
   * Price editing enabled.
   */
  priceEditingEnabled: boolean;

  /**
   * Variant pack information.
   */
  variantPack:
    | GetVariantPackQuery['getVariantPackInfoList'][number]
    | null
    | undefined;
}

/**
 * Interface representing a related variant pack.
 */
export type RelatedVariantPack =
  GetRelatedVariantPacks_VariantPackInfoFragment & {
    variant_pack: GetRelatedVariantPacks_VariantPackInfoFieldsFragment;
  };

/**
 * Sorts a list of variant packs by pack unit, then alphabetically by pack display string, then by variant pack UUID.
 *
 * @param items - An unsorted list of variant packs.
 *
 * @returns A list of sorted variant packs.
 */
export const sortRelatedVariantPacks = (
  items: RelatedVariantPack[],
  packSortConfig: ResolvedPackSort,
): RelatedVariantPack[] => {
  // Filter out items that don't have a variant pack
  const filteredItems = items.filter(
    (item) => item.variant_pack !== null && item.variant_pack !== undefined,
  ) as RelatedVariantPack[];

  // Sort by pack unit (single source of truth in pack-sort helper),
  // then alphabetically by pack display string.
  return filteredItems
    .map((item) => ({
      displayString: getPackDisplayString({
        maxUnitSize: item.variant_pack.pack.max_unit_size,
        minUnitSize: item.variant_pack.pack.min_unit_size,
        unit: item.variant_pack.pack.unit as PackUnit,
      }),
      id: item.variant_pack.uuid,
      item,
      unit: item.variant_pack.pack.unit as PackUnit,
    }))
    .sort((a, b) => comparePacksByUnit(a, b, packSortConfig))
    .map(({ item }) => item);
};

export const useVariantPack = (
  variantPackUUID: string,
  restaurant: EmployeeRestaurant,
  onErrorMessage: string,
): {
  /**
   * Error state of the variant pack.
   */
  error: ApolloError | undefined;

  /**
   * Loading state of the variant pack.
   */
  loading: boolean;
} & VariantPackInformation => {
  const { showErrorToast } = useToasts();
  const { getDynamicConfigValueForChat } = useGetDynamicConfigValueForChat();

  const { data, loading, error } = useWrappedQuery<
    GetVariantPackQuery,
    GetVariantPackQueryVariables
  >(GET_VARIANT_PACK_QUERY, {
    fetchPolicy: 'cache-and-network',
    onError: () => {
      showErrorToast(onErrorMessage);
    },
    variables: {
      restaurantUUID: restaurant.uuid,
      supplierUUID: restaurant.supplierUUID,
      variantPackUUID,
    },
  });

  const variantPackInfo = useMemo(() => {
    return data?.getVariantPackInfoList[0] ?? null;
  }, [data]);

  const name = variantPackInfo?.variant_pack?.item.name ?? '';

  const description = variantPackInfo?.variant_pack?.item.description ?? null;

  const externalItemId = useMemo(() => {
    return variantPackInfo?.variant_pack?.external_item_id ?? null;
  }, [variantPackInfo]);

  const photoURLs = useMemo(() => {
    return (variantPackInfo?.variant_pack?.item.photo_url_list ??
      []) as string[];
  }, [variantPackInfo]);

  const availableQuantity = variantPackInfo?.available_quantity ?? null;

  const availabilityStatus = useMemo(() => {
    return (variantPackInfo?.availability_status ??
      null) as VariantPackAvailabilityStatus | null;
  }, [variantPackInfo]);

  const badges = useMemo(() => {
    return (variantPackInfo?.variant_pack?.metadata?.badges ??
      []) as VariantPackInfoContext_GroupBadgeFieldsFragment[];
  }, [variantPackInfo]);

  const countryCode = data?.businesses_by_pk?.address?.country_code;

  const fallbackCurrencyCode = countryCode
    ? getCountryCurrencyCode(countryCode)
    : DEFAULT_CURRENCY_CODE;

  const currencyCode = variantPackInfo?.currency_code ?? fallbackCurrencyCode;

  const displayItemCode =
    data?.businesses_by_pk?.supplier_metadata?.display_item_code ?? false;

  const itemModifiersEnabled =
    data?.businesses_by_pk?.supplier_chats[0]?.item_modifiers_enabled ?? false;

  const costInfoDetails = useMemo(() => {
    return variantPackInfo?.cost_info?.details;
  }, [variantPackInfo]);

  const customerCostInMicros = useMemo(() => {
    const customerCostType = restaurant.customerCostType;

    return (
      costInfoDetails?.find((detail) => detail.type === customerCostType)
        ?.cost_in_micros ?? null
    );
  }, [costInfoDetails, restaurant.customerCostType]);

  const packUnit = useMemo(() => {
    return (variantPackInfo?.variant_pack?.pack.unit ??
      null) as PackUnit | null;
  }, [variantPackInfo]);

  const salesRepConfig = getDynamicConfigValueForChat(
    restaurant.chatUUIDs[0],
    'sales_rep_config',
  );

  const priceEditingEnabled = useMemo(() => {
    const disableOverrideEditing =
      salesRepConfig?.disable_override_editing ?? false;
    const salesRepGrossProfitEnabled = restaurant.salesRepGrossProfitEnabled;
    const salesRepPricingEnabled = restaurant.salesRepPricingEnabled;

    return (
      !disableOverrideEditing &&
      (salesRepGrossProfitEnabled || salesRepPricingEnabled) &&
      typeof customerCostInMicros === 'number'
    );
  }, [customerCostInMicros, restaurant, salesRepConfig]);

  return {
    availabilityStatus,
    availableQuantity,
    badges,
    currencyCode,
    customerCostInMicros,
    description,
    displayItemCode,
    error,
    externalItemId,
    itemModifiersEnabled,
    loading,
    name,
    packUnit,
    photoURLs,
    priceEditingEnabled,
    variantPack: data?.getVariantPackInfoList[0],
  };
};

export const useRelatedVariantPacks = ({
  supplierUUID,
  restaurantUUID,
  skip,
  displayOptions: { displayItemCode, itemModifiersEnabled },
  externalItemId,
  itemUUID,
  variantPackUUID,
  nextFulfillmentDate,
  onErrorMessage,
}: {
  displayOptions: { displayItemCode: boolean; itemModifiersEnabled: boolean };
  externalItemId?: string;
  itemUUID?: string;
  nextFulfillmentDate: string | null;
  onErrorMessage: string;
  restaurantUUID: string;
  skip: boolean;
  supplierUUID: string;
  variantPackUUID: string;
}) => {
  const { showErrorToast } = useToasts();

  const filters = useMemo(() => {
    if (itemModifiersEnabled) {
      return { item_uuid: { _eq: itemUUID } };
    }

    if (displayItemCode && externalItemId) {
      return {
        _or: [
          { external_item_id: { _eq: externalItemId } },
          { item_uuid: { _eq: itemUUID } },
        ],
      };
    }

    return {
      uuid: { _eq: variantPackUUID },
    };
  }, [
    displayItemCode,
    externalItemId,
    itemModifiersEnabled,
    itemUUID,
    variantPackUUID,
  ]);

  const {
    data: relatedVariantPacksUUIDsData,
    loading: relatedVariantPacksUUIDsLoading,
    error: relatedVariantPacksUUIDsError,
  } = useWrappedQuery(GET_RELATED_VARIANT_PACKS_IDS_QUERY, {
    onError: () => {
      showErrorToast(onErrorMessage);
    },
    skip,
    variables: {
      filters,
      restaurantUUID,
      supplierUUID,
    },
  });

  const variantPackUUIDs = useMemo(() => {
    return (
      relatedVariantPacksUUIDsData?.variants_packs?.map(
        (vp: GetRelatedVariantPacksIdsQuery['variants_packs'][number]) =>
          vp.uuid,
      ) ?? []
    );
  }, [relatedVariantPacksUUIDsData]);

  const {
    data: relatedVariantPacks,
    loading: relatedVariantPacksLoading,
    error: relatedVariantPacksError,
  } = useWrappedQuery(GET_RELATED_VARIANT_PACKS_QUERY, {
    onError: () => {
      showErrorToast(onErrorMessage);
    },
    skip: variantPackUUIDs.length === 0,
    variables: {
      deliveryDate: nextFulfillmentDate,
      restaurantUUID,
      supplierUUID,
      variantPackUUIDs,
    },
  });

  const packSortConfig = usePackSortConfig();
  const sortedRelatedVariantPacks = useMemo(() => {
    return sortRelatedVariantPacks(
      relatedVariantPacks?.getVariantPackInfoList ?? [],
      packSortConfig,
    );
  }, [relatedVariantPacks, packSortConfig]);

  return {
    error: relatedVariantPacksUUIDsError || relatedVariantPacksError,
    loading: relatedVariantPacksUUIDsLoading || relatedVariantPacksLoading,
    relatedVariantPacks: sortedRelatedVariantPacks,
  };
};
